/**
 * 학생 생성 모달 컴포넌트
 * - 이름, 전화번호, 학생ID, 비밀번호, 학년, 학교 입력
 * - 담당 강사 선택 (선택)
 * - 비밀번호 미입력 시 학생ID 사용
 */

import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, message } from 'antd';
import { userService, StudentCreateRequest } from '../../services/userService';
import type { User } from '../../types/user';

interface CreateStudentModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateStudentModal: React.FC<CreateStudentModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [instructors, setInstructors] = useState<User[]>([]);

  /**
   * 강사 목록 조회
   */
  const fetchInstructors = async () => {
    try {
      const response = await userService.getUsers({
        role: 'instructor',
        page_size: 100, // 최대 100명의 강사 조회
      });
      setInstructors(response.users);
    } catch (error) {
      console.error('강사 목록 조회 실패:', error);
    }
  };

  /**
   * 모달 열릴 때 강사 목록 조회
   */
  useEffect(() => {
    if (open) {
      fetchInstructors();
    }
  }, [open]);

  /**
   * 모달 닫기 처리
   */
  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  /**
   * 학생 생성 제출
   */
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const data: StudentCreateRequest = {
        name: values.name,
        phone: values.phone,
        student_id: values.student_id,
        password: values.password || undefined, // 미입력 시 student_id 사용
        instructor_id: values.instructor_id || undefined,
        grade: values.grade || undefined,
        school: values.school || undefined,
      };

      await userService.createStudent(data);
      message.success('학생이 생성되었습니다');
      handleClose();
      onSuccess();
    } catch (error: any) {
      if (error.response?.data?.detail) {
        message.error(error.response.data.detail);
      } else {
        message.error('학생 생성 실패');
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="학생 추가"
      open={open}
      onOk={handleSubmit}
      onCancel={handleClose}
      confirmLoading={loading}
      okText="생성"
      cancelText="취소"
      width={500}
    >
      <Form
        form={form}
        layout="vertical"
        autoComplete="off"
      >
        <Form.Item
          label="이름"
          name="name"
          rules={[
            { required: true, message: '이름을 입력하세요' },
            { min: 1, max: 100, message: '이름은 1-100자여야 합니다' },
          ]}
        >
          <Input placeholder="홍길동" />
        </Form.Item>

        <Form.Item
          label="학생 ID (로그인용)"
          name="student_id"
          rules={[
            { required: true, message: '학생 ID를 입력하세요' },
            { min: 1, max: 50, message: '학생 ID는 1-50자여야 합니다' },
          ]}
          extra="학생이 로그인할 때 사용하는 ID입니다"
        >
          <Input placeholder="student001" />
        </Form.Item>

        <Form.Item
          label="전화번호"
          name="phone"
          rules={[
            { pattern: /^\d{3}-\d{4}-\d{4}$/, message: '010-0000-0000 형식으로 입력하세요' },
          ]}
        >
          <Input placeholder="010-0000-0000" />
        </Form.Item>

        <Form.Item
          label="비밀번호 (선택)"
          name="password"
          extra="미입력 시 학생 ID와 동일한 비밀번호가 설정됩니다"
          rules={[
            { min: 6, message: '비밀번호는 최소 6자 이상이어야 합니다' },
          ]}
        >
          <Input.Password placeholder="최소 6자" />
        </Form.Item>

        <Form.Item
          label="담당 강사 (선택)"
          name="instructor_id"
        >
          <Select
            placeholder="담당 강사를 선택하세요"
            allowClear
            showSearch
            filterOption={(input, option) =>
              option?.label?.toString().toLowerCase().includes(input.toLowerCase()) || false
            }
            options={instructors.map((instructor) => ({
              label: `${instructor.name} (${instructor.email})`,
              value: instructor.id,
            }))}
          />
        </Form.Item>

        <Form.Item
          label="학년 (선택)"
          name="grade"
        >
          <Select
            placeholder="학년을 선택하세요"
            allowClear
            style={{ width: '100%' }}
          >
            <Select.Option value={1}>초1</Select.Option>
            <Select.Option value={2}>초2</Select.Option>
            <Select.Option value={3}>초3</Select.Option>
            <Select.Option value={4}>초4</Select.Option>
            <Select.Option value={5}>초5</Select.Option>
            <Select.Option value={6}>초6</Select.Option>
            <Select.Option value={7}>중1</Select.Option>
            <Select.Option value={8}>중2</Select.Option>
            <Select.Option value={9}>중3</Select.Option>
            <Select.Option value={10}>고1</Select.Option>
            <Select.Option value={11}>고2</Select.Option>
            <Select.Option value={12}>고3</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="학교 (선택)"
          name="school"
        >
          <Input placeholder="학교명" />
        </Form.Item>
      </Form>
    </Modal>
  );
};
